import { useCallback, useMemo } from "react";

import {
  useRemoteTableDataSource,
  TableIdProvider,
  IndexedRemoteTableDataSourceOptions,
  FilterSet,
  FilterValue,
} from "capitalrx-components-2.0/lib/general";

interface CustomDataSource<TRow extends object> {
  readonly url: string,
  readonly idProvider: TableIdProvider<TRow>,
  readonly idProviderType?: "string" | "array",
  readonly options: IndexedRemoteTableDataSourceOptions<TRow>;
  readonly searchLength: number;
}

export function useCustomDataSource<TRow extends object>(props: CustomDataSource<TRow>){
  const { url, idProvider, idProviderType = "string", options, searchLength } = props;
  const dataSource = useRemoteTableDataSource(url, idProvider, { ...options, authTokenRequired: false });

  const setFilter = useCallback((filter: FilterSet) => {
    const searchValue = filter[options.idFilterName];
    if (searchValue && typeof searchValue === "string" && searchValue.length < searchLength)
      return;

    if (typeof searchValue === "string") dataSource.setFilter(filter);
  }, [dataSource, options.idFilterName, searchLength]);

  const filterBy = useCallback((column: string, filterValue: FilterValue) => {
    if (column === options.idFilterName && typeof filterValue === "string" && filterValue.length < searchLength)
      return;

    if (idProviderType === "string") dataSource.filterBy(column, filterValue);
    if (idProviderType === "array") dataSource.filterBy(column, [filterValue]);
  }, [dataSource, idProviderType, options.idFilterName, searchLength]);

  return useMemo(() => ({
    ...dataSource,
    setFilter,
    filterBy,
  }), [dataSource, setFilter, filterBy]);
}
